import {
  put,
  call,
  select,
  take,
  delay,
  race,
  takeLatest,
} from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import { Action } from 'redux';
import { push } from 'connected-react-router';
import neptingService, {
  NeptingSuccessfullTransactionResponse,
} from 'services/Nepting/nepting.service';

import * as Sentry from '@sentry/react';

import {
  getReloadAmount,
  getTransactionId,
} from 'redux/reload/reload.selectors';
import { NeptingTransactionError } from 'services/Nepting/nepting.types';
import { getNeptingMerchantId } from 'redux/configuration/configuration.selectors';
import simpleLogService from 'services/SimpleLog';
import { setShouldReboot } from 'redux/hardware/hardware.actions';
import {
  displayNeptingErrorMessage,
  neptingPaymentSequenceEnded,
  startNeptingPayment,
  startNeptingInctivityWatcher,
} from './nepting.actions';

export const INACTIVITY_TIMEOUT = 1000 * 10;

function* paymentHandler(): Saga {
  const amount: ReturnType<typeof getReloadAmount> = yield select(
    getReloadAmount,
  );

  const neptingMerchantId: ReturnType<typeof getNeptingMerchantId> = yield select(
    getNeptingMerchantId,
  );

  const transactionId: ReturnType<typeof getTransactionId> = yield select(
    getTransactionId,
  );

  simpleLogService.info(
    `[Saga][nepting][paymentHandler] Starting payment handler - Amount: ${amount}, MerchantId: ${neptingMerchantId}`,
  );

  if (amount === undefined || neptingMerchantId === null) {
    simpleLogService.info(
      '[Saga][nepting][paymentHandler] Aborting - Missing amount or merchantId',
    );
    yield put<Action>(neptingPaymentSequenceEnded({ isSucess: false }));
    return;
  }

  if (!neptingService.initialized) {
    simpleLogService.info(
      '[Saga][nepting][paymentHandler] Aborting - Nepting service not initialized',
    );
    yield put<Action>(neptingPaymentSequenceEnded({ isSucess: false }));
  }

  yield put(push('/payment/pending'));

  try {
    simpleLogService.info(
      `[Saga][nepting][paymentHandler] Starting transaction - Service initialized: ${neptingService.initialized}`,
    );
    const ticket: NeptingSuccessfullTransactionResponse = yield call(() =>
      neptingService.startTransaction({
        cents: amount,
        neptingMerchantId,
      }),
    );

    simpleLogService.info(
      '[Saga][nepting][paymentHandler] Transaction successful - Ticket received',
    );

    yield put<Action>(
      neptingPaymentSequenceEnded({
        isSucess: true,
        ticket: ticket.customerTicket,
      }),
    );
  } catch (e) {
    const error = e as NeptingTransactionError;
    simpleLogService.info(
      `[Saga][nepting][paymentHandler] Transaction error - Code: ${error.code}, Message: ${error.message}`,
    );

    if (['104', '105'].includes(error.code)) {
      simpleLogService.info(
        `[Saga][nepting][paymentHandler] TPE not responding, sending sentry.. ${transactionId} ${error.transactionId}`,
      );

      Sentry.captureException('TPE not responding', {
        contexts: {
          transaction: {
            transactionDraftId: transactionId,
            neptingTransactionId: error.transactionId,
          },
        },
      });

      yield put<Action>(setShouldReboot(true));
      yield put<Action>(neptingPaymentSequenceEnded({ isSucess: false }));
      return;

      // if there are customer blocking issues on the payment terminal screen, add a timeout that displays a cancel button so the end customer can unblock themselves
      // Montrer un message de 'Probleme de hardware avec la borne, nous allons redemarer cette dernière'
    }

    yield put<Action>(displayNeptingErrorMessage(error.message ?? null));
    yield put(push('/payment/error'));
    yield put<Action>(startNeptingInctivityWatcher());
  }
}

function* neptingInactivityWatcher(): Saga {
  const { inactive } = yield race({
    startPayment: take(getType(startNeptingPayment)),
    inactive: delay(INACTIVITY_TIMEOUT),
  });

  if (inactive) {
    yield put<Action>(neptingPaymentSequenceEnded({ isSucess: false }));
  }
}

export function* watchNeptingActions(): Saga {
  yield takeLatest(getType(startNeptingPayment), paymentHandler);
  yield takeLatest(
    getType(startNeptingInctivityWatcher),
    neptingInactivityWatcher,
  );
}
